import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import {
  generateSimulationDescriptionAsync,
  getGTOSuggestedCountriesAsync,
  getSimulationComparisonDataAsync,
  getTrendDataByWorkspaceAsync,
  saveSimulationBOMAsync,
} from 'src/services/costModelling/skuShouldCostWorkbench.services'
import {
  orderTrendData,
  transformSimulationComparisonData,
} from 'src/views/pages/costModelling/utils/transformCostBreakdownData'
import { getSimulationListAsync } from '../../../../../services/costModelling/skuShouldCostWorkbench.services'
import {
  Currency_Data,
  timePeriodSimulationTrend,
} from 'src/constants/componentConstants/costModelling/skuShouldCostWorkbench'

const initialState = {
  home: {
    homeDataLoading: false,
    simulationList: [],
    error: null,
  },
  compare: {
    compareDataLoading: false,
    simulationComparisonData: [],
    error: null,
  },
  trend: {
    trendDataLoading: false,
    trendData: [],
    anchorDate: null,
    filters: {
      currentSelectedCurrency: Currency_Data[0],
      timePeriod: timePeriodSimulationTrend[0],
      selectedSimulation: [],
      exchangeRate: [],
    },
    error: null,
  },
  gtoSuggestionLoader: false,
  gtoSuggestedCountries: [],
  tabSwitchDisabled: false,
  suppressHomeApiFetch: false,
  loadComparisonAndTrendData: false,
}

const getWhatIfSimulationDataSlice = createSlice({
  name: 'get-what-if-compare',
  initialState,
  extraReducers: (builder) => {
    builder.addMatcher(isAnyOf(getTrendDataByWorkspaceAsync.pending), (state) => {
      state.trend.trendDataLoading = true
    })
    builder.addMatcher(isAnyOf(getTrendDataByWorkspaceAsync.fulfilled), (state, action) => {
      state.trend.trendDataLoading = false
      state.trend.trendData = orderTrendData(action?.payload?.data)
      state.trend.filters.selectedSimulation =
        action?.payload?.data?.map((ele) => ({
          label: ele?.simulation_name,
          value: ele?.simulation_name,
        })) || []
      state.trend.error = null
      state.loadComparisonAndTrendData = false
    })
    builder.addMatcher(isAnyOf(getTrendDataByWorkspaceAsync.rejected), (state, action) => {
      state.trend.trendDataLoading = false
      state.trend.error = action?.payload?.message
      state.loadComparisonAndTrendData = false
    })
    builder.addMatcher(isAnyOf(getSimulationComparisonDataAsync.pending), (state) => {
      state.compare.compareDataLoading = true
    })
    builder.addMatcher(isAnyOf(getSimulationComparisonDataAsync.fulfilled), (state, action) => {
      state.compare.compareDataLoading = false
      state.compare.simulationComparisonData = transformSimulationComparisonData(
        action?.payload?.data,
      )
      state.compare.error = null
      state.loadComparisonAndTrendData = false
    })
    builder.addMatcher(isAnyOf(getSimulationComparisonDataAsync.rejected), (state, action) => {
      state.home.compareDataLoading = false
      state.home.error = action?.payload?.message
      state.loadComparisonAndTrendData = false
    })

    //GTO Suggested Countries
    builder.addMatcher(isAnyOf(getGTOSuggestedCountriesAsync.pending), (state) => {
      state.gtoSuggestionLoader = true
    })
    builder.addMatcher(isAnyOf(getGTOSuggestedCountriesAsync.fulfilled), (state, action) => {
      state.gtoSuggestionLoader = false
      state.gtoSuggestedCountries = action?.payload?.data || []
    })
    builder.addMatcher(isAnyOf(getGTOSuggestedCountriesAsync.rejected), (state, action) => {
      state.gtoSuggestionLoader = false
    })

    builder.addMatcher(isAnyOf(getSimulationListAsync.pending), (state) => {
      state.home.simulationList = []
      state.home.homeDataLoading = true
      state.tabSwitchDisabled = true
    })
    builder.addMatcher(isAnyOf(getSimulationListAsync.fulfilled), (state, action) => {
      state.home.homeDataLoading = false
      state.home.simulationList = action?.payload?.data?.map((ele, index) => {
        return {
          simulationID: ele?.id,
          title: ele?.name,
          subtitle: ele?.category,
          cost: ele?.should_cost,
          country: ele?.country_of_origin,
          description: ele?.description,
          createdOn: ele?.created_date,
          hasError: false,
          missingIndexTickerCount: ele?.missing_index_ticker_count,
          missingIndices: ele?.missing_index_tickers,
          isLoading: false,
          gtoSuggestedCost: ele?.gto_suggested_cost,
          originalShouldCost: ele?.original_should_cost,
          isSimulationCreatingLoader: false,
          isSimulationValid:
            ele?.category?.toLowerCase() === 'base should cost' ? true : ele?.is_simulation_valid,
          gtoSuggestedTickers: ele?.gto_suggested_tickers,
        }
      })
      state.home.error = null
      const isBaseShouldCost =
        action?.payload?.data?.[0]?.category?.toLowerCase() === 'base should cost'
      const hasMultipleCompleteEntries =
        action?.payload?.data?.filter((ele) => ele?.missing_index_ticker_count === 0).length > 1
      if (action?.payload?.data?.length > 1 && isBaseShouldCost && hasMultipleCompleteEntries) {
        state.tabSwitchDisabled = false
      }
    })
    builder.addMatcher(isAnyOf(saveSimulationBOMAsync.pending), (state, action) => {
      state.loadComparisonAndTrendData = false
    })
    builder.addMatcher(isAnyOf(saveSimulationBOMAsync.fulfilled), (state, action) => {
      state.home.simulationList = state.home.simulationList.map((simulation) => {
        const ele = action?.payload?.data
        return ele && simulation.simulationID === ele?.simulation_id
          ? {
              ...simulation,
              title: ele?.simulation_name,
              subtitle: ele?.category,
              cost: ele?.should_cost,
              country: ele?.country_of_origin,
              description: ele?.description,
              createdOn: ele?.created_date,
              hasError: false,
              missingIndexTickerCount: ele?.missing_index_ticker_count,
              missingIndices: ele?.missing_index_tickers,
              isLoading: false,
              gtoSuggestedCost: ele?.gto_suggested_cost_at_fiscal,
              originalShouldCost: ele?.original_should_cost,
              isSimulationCreatingLoader: false,
              isSimulationValid:
                ele?.category?.toLowerCase() === 'base should cost'
                  ? true
                  : ele?.is_simulation_valid,
              gtoSuggestedTickers: ele?.gto_suggested_tickers,
            }
          : simulation
      })
      state.loadComparisonAndTrendData = true
    })
    builder.addMatcher(isAnyOf(generateSimulationDescriptionAsync.pending), (state, action) => {
      state.loadComparisonAndTrendData = false
    })
    builder.addMatcher(isAnyOf(generateSimulationDescriptionAsync.fulfilled), (state, action) => {
      state.home.simulationList = state.home.simulationList.map((simulation) => {
        const ele = action?.payload?.data?.find(
          (updatedEle) => String(simulation.simulationID) === String(updatedEle?.simulation_id),
        )
        return ele
          ? {
              ...simulation,
              description: ele?.description,
            }
          : simulation
      })
      state.loadComparisonAndTrendData = true
    })
    builder.addMatcher(isAnyOf(getSimulationListAsync.rejected), (state, action) => {
      state.home.simulationList = []
      state.home.homeDataLoading = false
      state.home.error = action?.payload?.message
    })
  },
  reducers: {
    //set home simulation list
    setSimulationList: (state, action) => {
      state.home.simulationList = action.payload
    },

    setTabSwitchDisabled: (state, action) => {
      state.tabSwitchDisabled = action.payload
    },

    //trendData setter
    setTrendData: (state, action) => {
      state.trend.trendData = action.payload
    },

    //set home
    setHome: (state, action) => {
      state.home = action.payload
    },

    //set compare
    setCompare: (state, action) => {
      state.compare = action.payload
    },

    //setTrend state
    setTrend: (state, action) => {
      state.trend = action.payload
    },

    //set trend filters
    setTrendFilters: (state, action) => {
      state.trend.filters = { ...state.trend.filters, ...action.payload }
    },

    //compareData setter
    setSimulationComparisonData: (state, action) => {
      state.compare.simulationComparisonData = action.payload
    },

    //reset simulation list
    resetSimulationList: (state) => {
      state.home.simulationList = initialState.home.simulationList
    },

    //resetter for  trendData
    resetTrend: (state) => {
      state.trend = initialState.trend
    },

    // resetter for simulation comparison data
    resetCompare: (state) => {
      state.compare = initialState.compare
    },

    //reset state
    resetSimulationData: (state) => {
      Object.assign(state, initialState)
    },

    //suppress home api state
    setSuppressHomeApiFetch: (state, action) => {
      state.suppressHomeApiFetch = action?.payload
    },
  },
})

export const {
  setSimulationList,
  setTabSwitchDisabled,
  setTrendData,
  setSimulationComparisonData,
  setTrend,
  setHome,
  setCompare,
  setTrendFilters,
  resetTrend,
  resetCompare,
  resetSimulationList,
  resetSimulationData,
  setSuppressHomeApiFetch,
} = getWhatIfSimulationDataSlice.actions

export default getWhatIfSimulationDataSlice.reducer
